/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

/* eslint-disable max-classes-per-file */
import React from 'react';
import _ from 'lodash';
import { decorate, action, observable, computed, runInAction } from 'mobx';
import { observer, inject, Observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Container } from 'semantic-ui-react';
import { niceNumber, swallowError } from '@amzn/base-ui/dist/helpers/utils';

import { isStoreError, isStoreNew, isStoreLoading, isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import { categories } from '../../models/studies/categories';
import StudiesTab from './StudiesTab';
import CreateStudy from './CreateStudy';
import KeyPairsList from '@amzn/key-pair-mgmt-ui/dist/parts/key-pairs/KeyPairsList';

// expected props
// - studiesStoresMap (via injection)
// - userStore (via injection)
class StudiesPage extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.activeTabId = null; // null means first applicable tab
    });
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    // Eagerly load all study stores so tab counts are available immediately
    _.forEach(categories, category => {
      const store = this.getStudiesStore(category);
      if (store && !isStoreReady(store)) {
        swallowError(store.load());
      }
    });
    // Also load key pairs store for the SSH Keys tab count
    const keyPairsStore = this.props.keyPairsStore;
    if (keyPairsStore && !isStoreReady(keyPairsStore)) {
      swallowError(keyPairsStore.load());
    }
  }

  getStudiesStore(category) {
    return this.props.studiesStoresMap[category.id];
  }

  get isAdmin() {
    return _.get(this.props.userStore, 'user.isAdmin', false);
  }

  get showAsResearcher() {
    return this.isAdmin && this.props.userStore.researcherView;
  }

  get canCreateStudy() {
    return _.get(this.props.userStore, 'user.capabilities.canCreateStudy', true);
  }

  get isExternalUser() {
    return _.get(this.props.userStore, 'user.isExternalUser', true);
  }

  get hasProjects() {
    return _.get(this.props.userStore, 'user.hasProjects', true);
  }

  get applicableTabs() {
    const tabs = [];

    // Combined tab — includes my-studies (unless external user) and organization (excluding licenses)
    const combinedStoreIds = [];
    if (!this.isExternalUser) combinedStoreIds.push(categories.myStudies.id);
    combinedStoreIds.push(categories.organization.id);
    tabs.push({ id: 'combined-studies', name: 'Studies', storeIds: combinedStoreIds, filter: s => !s.isLicenseStudy });

    // Licenses tab — organization studies flagged as license
    tabs.push({ id: 'licenses', name: 'Licenses', storeIds: [categories.organization.id], filter: s => s.isLicenseStudy });

    // Open Data tab
    tabs.push({ id: categories.openData.id, name: 'Open Data', storeIds: [categories.openData.id] });

    // SSH Keys tab
    tabs.push({ id: 'ssh-keys', name: 'SSH Keys', component: KeyPairsList });

    return tabs;
  }

  handleTabChange = (categoryId) => {
    this.activeTabId = categoryId;
  };

  render() {
    return (
      <Container className="mt3">
        {this.renderTitle()}
        {this.renderTabs()}
        {this.renderActiveTabContent()}
      </Container>
    );
  }

  renderTitle() {
    const canCreateStudy = this.canCreateStudy;
    const hasProjects = this.hasProjects;
    const showCreateStudy = canCreateStudy && hasProjects && !this.showAsResearcher;

    if (!showCreateStudy) return null;

    return (
      <div className="mb3 flex" style={{ justifyContent: 'flex-end' }}>
        <CreateStudy />
      </div>
    );
  }

  getTabCount(tab) {
    if (tab.component) {
      // SSH Keys tab -- get count from keyPairsStore
      if (tab.id === 'ssh-keys') {
        const store = this.props.keyPairsStore;
        if (store && !isStoreError(store) && !isStoreNew(store) && !isStoreLoading(store)) {
          return store.list.length;
        }
      }
      return null;
    }
    let total = 0;
    let allReady = true;
    _.forEach(tab.storeIds, storeId => {
      const store = this.props.studiesStoresMap[storeId];
      if (store && !isStoreError(store) && !isStoreNew(store) && !isStoreLoading(store)) {
        if (tab.filter) {
          total += store.list.filter(tab.filter).length;
        } else {
          total += store.total;
        }
      } else {
        allReady = false;
      }
    });
    return allReady ? total : null;
  }

  renderTabs() {
    const tabs = this.applicableTabs;
    const activeId = this.activeTabId || tabs[0]?.id;

    return (
      <div className="study-tabs">
        {tabs.map(tab => {
          const isActive = tab.id === activeId;
          const count = this.getTabCount(tab);

          return (
            <button
              key={tab.id}
              className={`study-tab${isActive ? ' active' : ''}`}
              onClick={() => this.handleTabChange(tab.id)}
              data-testid="table-tab"
            >
              {tab.name}
              {count !== null && <span className="study-tab-count">{niceNumber(count)}</span>}
            </button>
          );
        })}
      </div>
    );
  }

  renderActiveTabContent() {
    const tabs = this.applicableTabs;
    const activeId = this.activeTabId || tabs[0]?.id;
    const tab = _.find(tabs, ['id', activeId]);
    if (!tab) return null;

    // Component-based tab (e.g. SSH Keys)
    if (tab.component) {
      const TabComponent = tab.component;
      return <TabComponent key={tab.id} />;
    }

    return (
      <Observer>
        {() => <StudiesTab key={tab.id} tab={tab} />}
      </Observer>
    );
  }

}

decorate(StudiesPage, {
  activeTabId: observable,
  canCreateStudy: computed,
  hasProjects: computed,
  isExternalUser: computed,
  isAdmin: computed,
  showAsResearcher: computed,
  applicableTabs: computed,
  handleTabChange: action,
});

export default inject('studiesStoresMap', 'userStore', 'keyPairsStore')(withRouter(observer(StudiesPage)));
